import { useMemo, useRef } from "react"
import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useVirtualizer } from "@tanstack/react-virtual"

import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"

import { UsageBar } from "./UsageBar"
import { cssVars, DashboardResourceEntry, ResourceEntry } from "./resourceDashboardTypes"

const ROW_HEIGHT_PX = 36
const SCROLL_MAX_HEIGHT = "24rem"

type ResourceUsageBarListProps<TEntry extends ResourceEntry> = Readonly<{
    entries: ReadonlyArray<DashboardResourceEntry<TEntry>>
    getValue: (entry: DashboardResourceEntry<TEntry>) => number
    axisMax: number
    getColor: (entry: DashboardResourceEntry<TEntry>) => string
    onResourceClick: (resourceId: number, resourceType: ResourceType) => void
    formatValue?: (value: number) => string
}>

const useStyles = makeStyles()((theme) => ({
    scrollBox: {
        position: "relative",
        maxHeight: SCROLL_MAX_HEIGHT,
        overflowY: "auto",
    },
    spacer: {
        position: "relative",
        width: "100%",
        height: "var(--spacer-height)",
    },
    row: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: `${ROW_HEIGHT_PX / 16}rem`,
        display: "grid",
        gridTemplateColumns: "minmax(8rem, 16rem) 1fr",
        gap: "1rem",
        alignItems: "center",
        paddingRight: ".25rem",
        borderRadius: ".25rem",
        cursor: "pointer",
        transform: "translateY(var(--row-offset))",
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[50],
        },
    },
    name: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[700],
    },
}))

export function ResourceUsageBarList<TEntry extends ResourceEntry>({
    entries,
    getValue,
    axisMax,
    getColor,
    onResourceClick,
    formatValue,
}: ResourceUsageBarListProps<TEntry>) {
    const { classes } = useStyles()

    const scrollRef = useRef<HTMLDivElement>(null)

    const sortedEntries = useMemo(() => entries.slice().sort((a, b) => getValue(b) - getValue(a)), [entries, getValue])

    const rowVirtualizer = useVirtualizer({
        count: sortedEntries.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => ROW_HEIGHT_PX,
        overscan: 10,
    })

    const totalSize = rowVirtualizer.getTotalSize()
    const spacerStyle = useMemo(() => cssVars({ "--spacer-height": `${totalSize}px` }), [totalSize])

    return (
        <Box ref={scrollRef} className={classes.scrollBox}>
            <Box className={classes.spacer} style={spacerStyle}>
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const entry = sortedEntries[virtualRow.index]
                    const value = getValue(entry)
                    const fillPct = axisMax > 0 ? (value / axisMax) * 100 : 0

                    return (
                        <Box
                            key={virtualRow.index}
                            className={classes.row}
                            style={cssVars({ "--row-offset": `${virtualRow.start}px` })}
                            onClick={() => onResourceClick(entry.resource.id, entry.resourceType)}
                            title={formatValue?.(value)}
                        >
                            <Typography variant="body2" className={classes.name}>
                                {entry.resource.displayName}
                            </Typography>
                            <UsageBar fillPct={fillPct} color={getColor(entry)} />
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}
